import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { Controller } from "react-hook-form";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
} from "@mui/material";
import { formatValue } from "react-currency-input-field";

import Input from "../../../../../components/inputs/Input";
import DropdownFilter from "../../../../../components/inputs/DropdownFilter";
import capitalizeFirstLetter from "../../../../../util/toCapitalize";
import { useKioskoConfig } from "../../../context/kiosko-config.context";
import {
  useGetApplianceFreeQuote,
  useGetDeviceCategories,
} from "../../../../../services/microservice/appliances/use-appliances";
import { IPolicies } from "../../../../../services/microservice/micelu/device/device-service.interface";
import {
  ALLOWED_APPLIANCE_CATEGORY_IDS,
  APPLIANCE_CATEGORY_DISPLAY_LABELS,
  APPLIANCE_CATEGORY_PLAN_KEYWORDS,
  APPLIANCE_MANUFACTURER,
} from "./constants";

// Flujo "otro dispositivo electrónico" (electrodomésticos) para MacCenter.
// A diferencia del flujo de celulares (imei + tac + catálogo de modelos),
// aquí el dispositivo se identifica por serial + categoría, y la cotización
// es "libre" (por valor asegurado), no por modelo de catálogo: el agente
// digita marca/modelo comercial a mano. MacCenter solo vende Apple, así que
// el fabricante queda fijo (no editable) y la categoría se limita a lo que
// MacCenter realmente vende (laptop/smartwatch/tablet).
const ApplianceDeviceComponent = ({
  control,
  watch,
  setValue,
  errors,
  clearErrors,
  setSelectedPlan,
  setPlanData,
}) => {
  const config = useKioskoConfig();
  const [error, setError] = useState<string>("");
  // Todos los planes que devolvió la última cotización "libre" (el endpoint
  // no filtra por categoría). `infoPolicies` (abajo) es el subconjunto que
  // corresponde a la categoría elegida.
  const [rawPolicies, setRawPolicies] = useState<IPolicies[] | null>(null);

  const { data: categoriesRes } = useGetDeviceCategories();
  const { mutateAsync: getFreeQuote, isLoading: loadingPolicies } =
    useGetApplianceFreeQuote(config.sponsorID);

  // El label que ve el agente es solo visual (iPad/Mac/Apple Watch); el
  // request sigue mandando el `idSystem` real (TABLET/LAPTOP/SMARTWATCH) sin
  // tocar, porque `category.idSystem` (no `.name`) es lo que se envía.
  const allowedCategories = (categoriesRes?.data?.categories || [])
    .filter((category) =>
      ALLOWED_APPLIANCE_CATEGORY_IDS.includes(category.idSystem),
    )
    .map((category) => ({
      ...category,
      name: APPLIANCE_CATEGORY_DISPLAY_LABELS[category.idSystem] || category.name,
    }));

  const selectedCategoryId: string | undefined = watch("category")?.idSystem;

  // GET .../policy/free devuelve los planes de TODAS las categorías del
  // sponsor ("Plan 1 Apple Watch", "Plan 1 iPad", "Plan 1 Mac", ...); se
  // filtra en el front por el nombre del plan según la categoría elegida
  // (ver APPLIANCE_CATEGORY_PLAN_KEYWORDS).
  const infoPolicies = useMemo<IPolicies[] | null>(() => {
    if (!rawPolicies) return null;
    const keywords = selectedCategoryId
      ? APPLIANCE_CATEGORY_PLAN_KEYWORDS[selectedCategoryId]
      : undefined;
    if (!keywords) return rawPolicies;
    return rawPolicies.filter((policy) =>
      keywords.some((keyword) =>
        (policy.name || "").toLowerCase().includes(keyword),
      ),
    );
  }, [rawPolicies, selectedCategoryId]);

  const getErrorMessage = (value: unknown): string => {
    if (typeof value === "string") return value;
    if (
      value &&
      typeof value === "object" &&
      "message" in (value as { message?: unknown })
    ) {
      return getErrorMessage((value as { message?: unknown }).message);
    }
    return "";
  };

  // Al cambiar la categoría se limpia el plan seleccionado (la lista de
  // planes se re-filtra sola vía el `useMemo` de arriba, sin recotizar).
  useEffect(() => {
    setValue("plan", "" as any);
    setValue("valuePolicie", "");
  }, [watch("category")]);

  // La cotización "libre" solo depende del valor asegurado + sponsor (no del
  // modelo/fabricante), según la colección de servicios nuevos.
  useEffect(() => {
    const insuredValue = watch("insuredValue");
    const numericValue = Number(
      (insuredValue || "").replace(/,/g, "").replace("$", ""),
    );
    setValue("plan", "" as any);
    setValue("valuePolicie", "");
    clearErrors("plan");
    if (insuredValue && numericValue > 2000) {
      getFreeQuote(String(numericValue))
        .then((res) => {
          setRawPolicies(res.data.policies);
          setError("");
        })
        .catch((error) => {
          if (error.status === 400) {
            setError(
              getErrorMessage(error?.response?.data?.error?.message) ||
                "Ocurrio un error inesperado",
            );
          } else {
            setError("Ocurrio un error inesperado");
          }
        });
    } else {
      setRawPolicies(null);
    }
  }, [watch("insuredValue")]);

  useEffect(() => {
    if (watch("plan")) {
      const plan = watch("plan") as any;
      if (!_.isEmpty(plan) && infoPolicies && infoPolicies.length > 0) {
        const currentPlan = infoPolicies.filter((pol) =>
          pol.pricingOptions.some((opt) => opt.id === Number(plan)),
        )[0];
        const selectedOption = currentPlan?.pricingOptions.filter(
          (opt) => opt.id === Number(plan),
        )[0];
        if (selectedOption) {
          setValue(
            "valuePolicie",
            `$ ${selectedOption.paymentAmountWithTaxes
              .toString()
              .replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1,")}`,
          );
          if (setPlanData && currentPlan) {
            setPlanData(currentPlan);
          }
        }
      }
    }
  }, [watch("plan")]);

  useEffect(() => {
    if (watch("insuredValue") === "$") {
      setValue("insuredValue", "");
    }
  }, [watch("insuredValue")]);

  const displayCurrency = (val: string) => {
    // Sin valor, o sin dígitos después de sacar "$"/",", no hay nada que
    // formatear — evita que quede mostrando "$NaN" al borrar todo el precio.
    if (!val || val === "$") return "";
    const currency = val.replace(/,/g, "").replace("$", "");
    if (!currency || Number.isNaN(Number(currency))) return "";
    const formatCurrency = formatValue({
      value: currency,
      groupSeparator: ",",
      decimalSeparator: ".",
      prefix: "$",
    });
    return formatCurrency;
  };

  return (
    <section className="w-full">
      <div className="mb-10 w-full">
        <div className="space-y-6">
          <div className="rounded-2xl border-2 border-gray bg-white p-4 shadow-sm">
            <h2 className="mb-3 text-lg font-semibold">Datos dispositivo</h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <DropdownFilter
                    {...field}
                    options={allowedCategories}
                    placeholder="Selecciona la categoría"
                    label="name"
                    city={false}
                    required
                    error={errors?.category?.idSystem?.message}
                  />
                )}
              />
              <Input
                classNameContainer="w-full mb-7"
                classNameInput="w-full"
                placeholder="Fabricante"
                value={APPLIANCE_MANUFACTURER}
                disabled
                readOnly
              />
              <Controller
                name="comercialName"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    classNameContainer="w-full mb-7"
                    classNameInput="w-full"
                    placeholder="Nombre comercial"
                    required
                    error={errors?.comercialName?.message}
                  />
                )}
              />
              <Controller
                name="applianceModel"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    classNameContainer="w-full mb-7"
                    classNameInput="w-full"
                    placeholder="Modelo"
                    required
                    error={errors?.applianceModel?.message}
                  />
                )}
              />
              <Controller
                name="serial"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    classNameContainer="w-full mb-7"
                    classNameInput="w-full"
                    placeholder="Serial"
                    required
                    error={errors?.serial?.message}
                  />
                )}
              />
              <Controller
                name="insuredValue"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    classNameContainer="w-full mb-7"
                    classNameInput="w-full"
                    placeholder="Valor cubierto"
                    required
                    error={errors?.insuredValue?.message}
                    value={displayCurrency(watch("insuredValue"))}
                  />
                )}
              />
            </div>
          </div>

          <div className="rounded-2xl border-2 border-gray bg-white p-4 shadow-sm">
            <h2 className="mb-3 text-lg font-semibold">Datos plan</h2>
            <Controller
              name="plan"
              control={control}
              render={({ field }) => (
                <FormControl component="fieldset" className="mb-10">
                  <p className="my-0 py-0 text-base">
                    Seleccione plan{" "}
                    <span className="text-center text-3xl text-critical">
                      *
                    </span>
                    {errors?.plan && (
                      <span className="my-0 py-0 text-base text-critical">
                        {getErrorMessage(errors?.plan?.message)}
                      </span>
                    )}
                  </p>
                  {loadingPolicies ? (
                    <Stack>
                      {[...Array(infoPolicies?.length || 2)].map((_, i) => (
                        <Stack
                          key={i}
                          sx={{
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "flex-start",
                          }}
                        >
                          <Skeleton variant="circular" width={20} height={20} />
                          <Skeleton
                            variant="text"
                            width="80%"
                            sx={{ marginLeft: "12px" }}
                          />
                        </Stack>
                      ))}
                    </Stack>
                  ) : infoPolicies?.length > 0 ? (
                    <RadioGroup
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="mb-3"
                    >
                      {infoPolicies.map((policy) =>
                        policy.pricingOptions.map((option) => (
                          <FormControlLabel
                            key={option.id}
                            value={option.id}
                            control={<Radio />}
                            onChange={() => setSelectedPlan(policy.id)}
                            label={
                              policy.name
                                ? `${policy?.name} - ${capitalizeFirstLetter(option?.paymentFrequency)}`
                                : "Sin nombre"
                            }
                          />
                        )),
                      )}
                    </RadioGroup>
                  ) : (
                    <p className="my-0 mb-10 py-0 text-base">
                      No hay planes disponibles
                    </p>
                  )}
                </FormControl>
              )}
            />
            <Controller
              name="valuePolicie"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  classNameContainer="w-full mb-7"
                  classNameInput="w-full"
                  placeholder="Valor cobertura IVA incluido"
                  readOnly
                  error={errors?.valuePolicie?.message}
                />
              )}
            />

            {error && (
              <p className="text-md text-critical">{getErrorMessage(error)}</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ApplianceDeviceComponent;
